import React, { useEffect, useImperativeHandle, useRef, useState } from 'react'
import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { getChargebeeInstance } from 'src/components/pages/settings/Settings/BillingOptions/util/get-chargebee-instance'
import { Sentry } from 'src/util/appSentry'

export interface ChargebeeCardFieldsHandle {
  /**
   * Tokenizes the card inside Chargebee's iframes and resolves the temp token. Rejects with a
   * displayable message when the fields are incomplete or the card is rejected at tokenization.
   */
  tokenize: (cardholder: { firstName?: string; lastName?: string }) => Promise<string>
}

const useStyles = makeStyles<Theme>(
  (theme) => ({
    row: {
      display: 'flex',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column'
      }
    },
    field: {
      flex: 1,
      minWidth: 0
    },
    label: {
      marginBottom: theme.spacing(0.5),
      display: 'block'
    },
    // Chargebee renders an iframe into this node. It has no intrinsic height, so the box has to
    // look like the app's own inputs on its own.
    input: {
      border: `1px solid ${theme.palette.grey[400]}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5),
      height: 48,
      background: theme.palette.common.white
    }
  }),
  { name: 'ChargebeeCardFields' }
)

// Matches the app's own inputs closely enough that the iframe does not read as foreign.
const FIELD_STYLE = {
  base: {
    color: '#3c4858',
    fontSize: '16px',
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    '::placeholder': { color: '#aaaaaa' }
  },
  invalid: { color: '#d32f2f' }
}

/**
 * Card number / expiry / CVV as Chargebee-hosted iframes (ENG-1532).
 *
 * These are deliberately NOT our own inputs. The card number is typed into an iframe served by
 * Chargebee and tokenized there, so no PAN ever reaches our JavaScript or our servers, and the
 * app stays at PCI SAQ-A. First and last name are ordinary fields and live in the parent dialog.
 *
 * Uses the raw chargebee.js `createComponent` API through the loader the billing settings page
 * already uses, rather than adding @chargebee/chargebee-js-react-wrapper — same script, one fewer
 * dependency, and one Chargebee.init for the whole app.
 */
const ChargebeeCardFields = React.forwardRef<ChargebeeCardFieldsHandle, { onReady?: () => void }>(
  function ChargebeeCardFields({ onReady }, ref) {
    const classes = useStyles()
    const componentRef = useRef<any>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [mountError, setMountError] = useState<string | null>(null)

    useEffect(() => {
      let cancelled = false
      let component: any

      async function mount() {
        // Without a publishable key Chargebee.init produces an instance that mounts but cannot
        // tokenize, so the failure would only surface after the user typed a card in and pressed
        // Pay Now. Fail loudly here instead.
        if (!process.env.CHARGEBEE_PUBLISHABLE_KEY) {
          setMountError('Card payments are not configured for this environment. Please contact support.')
          Sentry.captureException(new Error('CHARGEBEE_PUBLISHABLE_KEY is not set — the card form cannot tokenize'))
          return
        }

        try {
          const cbInstance = await getChargebeeInstance()
          if (cancelled) return

          // Required before createComponent — chargebee.js loads the components module on demand,
          // and createComponent throws without it. This is what the official React wrapper's
          // ComponentGroup does before rendering any field.
          await cbInstance.load('components')
          if (cancelled) return

          component = cbInstance.createComponent('card', { style: FIELD_STYLE })
          component.createField('number').at('#ps-cb-card-number')
          component.createField('expiry').at('#ps-cb-card-expiry')
          component.createField('cvv').at('#ps-cb-card-cvv')

          // mount() takes the element that wraps the fields, not no argument. The individual
          // iframes go to their own `.at()` targets inside it.
          await component.mount(containerRef.current)
          if (cancelled) return

          componentRef.current = component
          onReady?.()
        } catch (error) {
          if (cancelled) return
          // Report the real cause. The previous message blamed an ad blocker for every failure,
          // which hid two genuine bugs in this mount sequence.
          Sentry.captureException(error)
          setMountError(
            'Unable to load the secure card form. Please refresh, or contact support if it keeps happening.'
          )
        }
      }

      mount()

      return () => {
        cancelled = true
        // Leaving iframes mounted after the dialog closes leaks them into the next open.
        try {
          component?.destroy?.()
        } catch {
          // Chargebee throws if it was never fully mounted. Nothing to clean up in that case.
        }
        componentRef.current = null
      }
    }, [onReady])

    useImperativeHandle(ref, () => ({
      async tokenize({ firstName, lastName }: { firstName?: string; lastName?: string } = {}) {
        if (!componentRef.current) {
          throw new Error('The secure card form is still loading. Please try again in a moment.')
        }

        const result = await componentRef.current.tokenize({ firstName, lastName })
        if (!result?.token) {
          throw new Error('Please check the card details and try again.')
        }

        return result.token as string
      }
    }))

    if (mountError) {
      return (
        <Typography variant="body2" color="error">
          {mountError}
        </Typography>
      )
    }

    return (
      <div className={classes.row} ref={containerRef}>
        <div className={classes.field}>
          <Typography variant="caption" className={classes.label}>
            Card Number
          </Typography>
          <div id="ps-cb-card-number" className={classes.input} />
        </div>
        <div className={classes.field}>
          <Typography variant="caption" className={classes.label}>
            Expiry
          </Typography>
          <div id="ps-cb-card-expiry" className={classes.input} />
        </div>
        <div className={classes.field}>
          <Typography variant="caption" className={classes.label}>
            CVV
          </Typography>
          <div id="ps-cb-card-cvv" className={classes.input} />
        </div>
      </div>
    )
  }
)

export default ChargebeeCardFields
